import { Trans, useTranslation } from 'react-i18next';
import { CSSProperties } from 'react';
import { motion } from 'framer-motion';

import HeartLockedShape from '@/components/common/svg/shapes/HeartLockedShape.tsx';
import SmallStarsShape from '@/components/common/svg/shapes/SmallStarsShape.tsx';
import UIConfig from '@/UIConfig.ts';
import ClockShape from '@/components/common/svg/shapes/ClockShape.tsx';

type LikesLimitPopupProps = {
  handleClose: () => void;
  openPaywall: () => void;
};

const LikesLimitPopup = ({
  handleClose,
  openPaywall
}: LikesLimitPopupProps) => {
  const { t } = useTranslation();

  return (
    <div style={containerStyles}>
      <div style={outerCircleContainerStyles}>
        <div style={outerCircleStyles} />
        <div style={innerCircleStyles} />
        <div style={shapeCircleStyles} />
        <HeartLockedShape
          width={56}
          height={56}
          style={heartLockedShapeStyles}
        />
        <div style={clockContainerStyles}>
          <ClockShape width={22} height={22} fillColor="#8858FF" />
        </div>
        <div style={starsContainerStyles}>
          <SmallStarsShape height={24} width={24} fillColor="#8858FF" />
        </div>
      </div>
      <h2 style={{ ...headerStyles, marginTop: '20px' }}>
        {t('likes_limit_popup.header')}
      </h2>
      <p style={{ ...descriptionStyles, marginTop: '20px' }}>
        <Trans
          i18nKey="likes_limit_popup.description"
          components={{
            br: <br />
          }}
        />
      </p>
      <motion.div
        style={{ ...premiumButtonContainerStyles, marginTop: '25px' }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {
          openPaywall();
          handleClose();
        }}
      >
        <span style={premiumButtonTextStyles}>
          {t('likes_limit_popup.premium_button')}
        </span>
      </motion.div>
      <motion.div
        style={{ ...passButtonContainerStyles, marginTop: '25px' }}
        whileTap={{ scale: 0.95 }}
        onClick={handleClose}
      >
        <span style={passButtonTextStyles}>
          {t('likes_limit_popup.pass_button')}
        </span>
      </motion.div>
    </div>
  );
};

export default LikesLimitPopup;

const containerStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: '400px',
  height: '100%',
  padding: '50px 0px 20px 0px',
  boxSizing: 'border-box',
  position: 'relative',
  overflow: 'hidden'
};

const outerCircleContainerStyles: CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '154px',
  height: '154px'
};

const outerCircleStyles: CSSProperties = {
  position: 'absolute',
  width: '152px',
  height: '152px',
  background: '#EDE4F5',
  opacity: 0.2,
  borderRadius: '50%',
  zIndex: 1
};

const innerCircleStyles: CSSProperties = {
  position: 'absolute',
  width: '125px',
  height: '125px',
  background: '#EDE6FF',
  borderRadius: '50%',
  zIndex: 2
};

const shapeCircleStyles: CSSProperties = {
  position: 'absolute',
  width: '102px',
  height: '102px',
  background: '#8858FF',
  borderRadius: '50%',
  zIndex: 2
};

const heartLockedShapeStyles: CSSProperties = {
  zIndex: 3
};

const starsContainerStyles: CSSProperties = {
  position: 'absolute',
  bottom: -15,
  right: -5,
  zIndex: 4
};

const clockContainerStyles: CSSProperties = {
  position: 'absolute',
  bottom: 0,
  right: 22,
  width: '34px',
  height: '34px',
  background: '#EDE6FF',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 4
};

const headerStyles: CSSProperties = {
  fontSize: '24px',
  fontWeight: 'bold',
  color: '#333',
  textAlign: 'center',
  margin: 0
};

const descriptionStyles: CSSProperties = {
  fontSize: '16px',
  color: '#666',
  textAlign: 'center',
  maxWidth: '350px',
  margin: 0
};

const premiumButtonContainerStyles: CSSProperties = {
  borderRadius: '50px',
  display: 'flex',
  minWidth: '250px',
  justifyContent: 'center',
  alignItems: 'center',
  background: UIConfig.Colors.GradientPremium,
  cursor: 'pointer'
};

const premiumButtonTextStyles: CSSProperties = {
  fontSize: '16px',
  fontWeight: 600,
  padding: '15px 12px',
  color: '#fff',
  fontFamily: UIConfig.Fonts.Primary
};

const passButtonContainerStyles: CSSProperties = {
  display: 'flex',
  width: '207px',
  height: '44px',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer'
};

const passButtonTextStyles: CSSProperties = {
  fontSize: '15px',
  fontWeight: 600,
  color: '#5A5555',
  opacity: 0.7,
  fontFamily: UIConfig.Fonts.Primary
};
